/**
 * Helper functions for VehicleInformationStep component
 */
import moment from 'moment';
import {
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
} from 'pagesv2/vehiclesv2/create-vehiclev2/CreateVehiclePageV2.constants';
import {
	DATE_FORMAT,
	RC_DOCUMENT_LABEL,
	INSURANCE_DOCUMENT_LABEL,
	CONSENT_LETTER_LABEL,
	VEHICLE_TYPE_LABEL,
	VEHICLE_NUMBER_LABEL,
	VALID_TILL_LABEL,
	OWNERSHIP_TYPE_LABEL,
	MAX_FILE_SIZE,
} from './VehicleInformationStep.constants';

/**
 * Validate vehicle form fields
 */
export const validateVehicleFields = (values, messageInstance) => {
	const requiredFields = [
		{ field: 'vehicle_type_id', label: VEHICLE_TYPE_LABEL },
		{ field: 'vehicle_number', label: VEHICLE_NUMBER_LABEL },
	];

	if (!values.skip_document_upload) {
		requiredFields.push(
			{ field: 'rc_document_key', label: RC_DOCUMENT_LABEL },
			{ field: 'rc_valid_till', label: `RC ${VALID_TILL_LABEL}` },
			{ field: 'insurance_document_key', label: INSURANCE_DOCUMENT_LABEL },
			{ field: 'insurance_valid_till', label: `Insurance ${VALID_TILL_LABEL}` },
			{ field: 'ownership_type', label: OWNERSHIP_TYPE_LABEL }
		);
	}

	// Add consent_letter_key as required only if ownership_type is "Aggregator"
	if (!values.skip_document_upload && values.ownership_type === 'Aggregator') {
		requiredFields.push({ field: 'consent_letter_key', label: CONSENT_LETTER_LABEL });
	}

	const missingFields = requiredFields
		.filter(({ field }) => {
			const value = values[field];
			return !value || (typeof value === 'string' && value.trim() === '');
		})
		.map(({ label }) => label);

	if (missingFields.length > 0) {
		const errorMessage = `Please fill in all required fields: ${missingFields.join(', ')}`;
		messageInstance.error(errorMessage);
		return false;
	}

	if (values.vehicle_number && !VEHICLE_NUMBER_PATTERN.test(values.vehicle_number)) {
		messageInstance.error(VEHICLE_NUMBER_MESSAGE);
		return false;
	}

	return true;
};

/**
 * Build the list of required {field,label} entries for an editing vehicle.
 * Document fields are skipped only when skipDocuments is true (vendor creation).
 */
const getEditingVehicleRequiredFields = (editingVehicle, skipDocuments) => {
	const requiredFields = [
		{ field: 'vehicle_type_id', label: VEHICLE_TYPE_LABEL },
		{ field: 'vehicle_number', label: VEHICLE_NUMBER_LABEL },
	];

	if (skipDocuments) {
		return requiredFields;
	}

	requiredFields.push(
		{ field: 'rc_document_key', label: RC_DOCUMENT_LABEL },
		{ field: 'rc_valid_till', label: `RC ${VALID_TILL_LABEL}` },
		{ field: 'insurance_document_key', label: INSURANCE_DOCUMENT_LABEL },
		{ field: 'insurance_valid_till', label: `Insurance ${VALID_TILL_LABEL}` },
		{ field: 'ownership_type', label: OWNERSHIP_TYPE_LABEL }
	);

	// Consent letter is required only if ownership_type is "Aggregator"
	if (editingVehicle.ownership_type === 'Aggregator') {
		requiredFields.push({ field: 'consent_letter_key', label: CONSENT_LETTER_LABEL });
	}

	return requiredFields;
};

/**
 * Validate GPS fields for an editing vehicle. Returns an error string or null.
 */
const getEditingVehicleGpsError = (editingVehicle) => {
	if (editingVehicle.gps_installed === undefined || editingVehicle.gps_installed === null) {
		return 'Please select GPS installation status';
	}
	if (editingVehicle.gps_installed === true && !editingVehicle.gps_provider_name) {
		return 'GPS Provider Name is required';
	}
	return null;
};

/**
 * A vehicle is "persisted" (already saved on the backend) when it has a real
 * integer id. Newly-added vehicles (added in the current session, not yet saved)
 * use a `vehicle_<timestamp>` key and have no id. Note: after saving, a vehicle
 * keeps its temporary key but gets a backend id — so the id alone is the source
 * of truth for "is this vehicle created".
 */
export const isPersistedVehicle = (vehicle) => Number.isInteger(vehicle?.id) && vehicle.id > 0;

/**
 * Validate editing vehicle fields
 */
export const validateEditingVehicle = (editingVehicle, messageInstance, allowSkip = false) => {
	if (!editingVehicle) {
		messageInstance.error('No vehicle data to validate');
		return false;
	}

	// Skip-document is only honored during vendor creation (allowSkip). On vendor
	// edit the documents are always mandatory regardless of the stored skip flag.
	const skipDocuments = allowSkip && Boolean(editingVehicle.skip_document_upload);

	const missingFields = getEditingVehicleRequiredFields(editingVehicle, skipDocuments)
		.filter(({ field }) => {
			const value = editingVehicle[field];
			return !value || (typeof value === 'string' && value.trim() === '');
		})
		.map(({ label }) => label);

	if (missingFields.length > 0) {
		messageInstance.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
		return false;
	}

	if (!skipDocuments) {
		const gpsError = getEditingVehicleGpsError(editingVehicle);
		if (gpsError) {
			messageInstance.error(gpsError);
			return false;
		}
	}

	if (
		editingVehicle.vehicle_number &&
		!VEHICLE_NUMBER_PATTERN.test(editingVehicle.vehicle_number)
	) {
		messageInstance.error(VEHICLE_NUMBER_MESSAGE);
		return false;
	}

	if (editingVehicle.puc_document_key && !editingVehicle.puc_valid_till) {
		messageInstance.error('PUC Valid Till is required when PUC document is uploaded');
		return false;
	}

	return true;
};

/**
 * Build vehicle object from form values
 */
export const buildVehicleFromFormValues = (values) => ({
	key: `vehicle_${Date.now()}`,
	vehicleType: values.vehicle_type || '',
	vehicleNumber: values.vehicle_number || '',
	rcDocument: !!values.rc_document_key,
	insurance: !!values.insurance_document_key,
	ownershipType: values.ownership_type || '',
	// Store all vehicle data for API submission
	vehicle_type: values.vehicle_type,
	vehicle_number: values.vehicle_number,
	rc_document_key: values.rc_document_key,
	rc_valid_till: values.rc_valid_till ? moment(values.rc_valid_till).format(DATE_FORMAT) : null,
	insurance_document_key: values.insurance_document_key,
	insurance_valid_till: values.insurance_valid_till
		? moment(values.insurance_valid_till).format(DATE_FORMAT)
		: null,
	puc_document_key: values.puc_document_key,
	puc_valid_till: values.puc_valid_till
		? moment(values.puc_valid_till).format(DATE_FORMAT)
		: null,
	ownership_type: values.ownership_type,
	consent_letter_key: values.consent_letter_key,
	skip_document_upload: !!values.skip_document_upload,
});

/**
 * Build editing vehicle object from vehicle data
 */
export const buildEditingVehicleFromVehicle = (vehicle) => ({
	vehicleType: vehicle.vehicleType || vehicle.vehicle_type || '',
	vehicleNumber: vehicle.vehicleNumber || vehicle.vehicle_number || '',
	vehicle_type: vehicle.vehicle_type || vehicle.vehicleType || '',
	vehicle_number: vehicle.vehicle_number || vehicle.vehicleNumber || '',
	rc_document_key: vehicle.rc_document_key || '',
	rc_valid_till: vehicle.rc_valid_till ? moment(vehicle.rc_valid_till, DATE_FORMAT) : null,
	insurance_document_key: vehicle.insurance_document_key || '',
	insurance_valid_till: vehicle.insurance_valid_till
		? moment(vehicle.insurance_valid_till, DATE_FORMAT)
		: null,
	puc_document_key: vehicle.puc_document_key || '',
	puc_valid_till: vehicle.puc_valid_till ? moment(vehicle.puc_valid_till, DATE_FORMAT) : null,
	ownership_type: vehicle.ownership_type || vehicle.ownershipType || '',
	consent_letter_key: vehicle.consent_letter_key || '',
	skip_document_upload: !!vehicle.skip_document_upload,
});

/**
 * Build updated vehicle object from editing vehicle
 */
export const buildUpdatedVehicleFromEditing = (selectedVehicle, editingVehicle) => ({
	...selectedVehicle,
	// Display fields
	vehicleType: editingVehicle.vehicle_type || editingVehicle.vehicleType,
	vehicleNumber: editingVehicle.vehicle_number || editingVehicle.vehicleNumber,
	ownershipType: editingVehicle.ownership_type,
	rcDocument: !!editingVehicle.rc_document_key,
	insurance: !!editingVehicle.insurance_document_key,
	// API fields
	vehicle_type: editingVehicle.vehicle_type,
	vehicle_number: editingVehicle.vehicle_number,
	rc_document_key: editingVehicle.rc_document_key,
	rc_valid_till: editingVehicle.rc_valid_till
		? moment(editingVehicle.rc_valid_till).format(DATE_FORMAT)
		: null,
	insurance_document_key: editingVehicle.insurance_document_key,
	insurance_valid_till: editingVehicle.insurance_valid_till
		? moment(editingVehicle.insurance_valid_till).format(DATE_FORMAT)
		: null,
	puc_document_key: editingVehicle.puc_document_key,
	puc_valid_till: editingVehicle.puc_valid_till
		? moment(editingVehicle.puc_valid_till).format(DATE_FORMAT)
		: null,
	ownership_type: editingVehicle.ownership_type,
	consent_letter_key: editingVehicle.consent_letter_key,
	skip_document_upload: !!editingVehicle.skip_document_upload,
});

/**
 * Get file key field name from field name
 */
export const getFileKeyFieldName = (fieldName) => {
	const fieldMap = {
		rc_doc: 'rc_document_key',
		insurance: 'insurance_document_key',
		puc_doc: 'puc_document_key',
		consent_letter: 'consent_letter_key',
	};
	return fieldMap[fieldName] || `${fieldName}_key`;
};

/**
 * Validate file size
 */
export const validateFileSize = (file, messageInstance) => {
	if (file.size > MAX_FILE_SIZE) {
		messageInstance.error(`File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`);
		return false;
	}
	return true;
};

/**
 * Get fields to clear for vehicle form
 */
export const getVehicleFieldsToClear = () => [
	'vehicle_type_id',
	'vehicle_number',
	'rc_doc_file',
	'rc_document_key',
	'rc_valid_till',
	'insurance_file',
	'insurance_document_key',
	'insurance_valid_till',
	'puc_doc_file',
	'puc_document_key',
	'puc_valid_till',
	'ownership_type',
	'consent_letter_file',
	'consent_letter_key',
	'gps_installed',
	'gps_provider_name',
	'skip_document_upload',
];

/**
 * Clear vehicle form fields
 */
export const clearVehicleFields = (setFieldValue, refs) => {
	const fieldsToClear = getVehicleFieldsToClear();

	fieldsToClear.forEach((field) => {
		setFieldValue(field, '');
	});

	// Reset file inputs
	if (refs.rcDocFileInputRef?.current) refs.rcDocFileInputRef.current.value = '';
	if (refs.insuranceFileInputRef?.current) refs.insuranceFileInputRef.current.value = '';
	if (refs.pucDocFileInputRef?.current) refs.pucDocFileInputRef.current.value = '';
	if (refs.consentLetterFileInputRef?.current) refs.consentLetterFileInputRef.current.value = '';
};

/**
 * Update vehicle in local state
 */
export const updateVehicleInLocalState = (
	selectedVehicle,
	editingVehicle,
	vehicles,
	setFieldValue,
	momentInstance,
	dateFormat,
	messageInstance,
	handleModalClose
) => {
	if (!selectedVehicle || !editingVehicle) {
		return;
	}

	// Get vehicle type name for display
	const vehicleTypeName = editingVehicle.vehicleType || editingVehicle.vehicle_type_name || '';

	const updatedVehicle = {
		...selectedVehicle,
		// Display fields
		vehicleType: vehicleTypeName,
		vehicleNumber: editingVehicle.vehicle_number || editingVehicle.vehicleNumber,
		ownershipType: editingVehicle.ownership_type,
		rcDocument: !!editingVehicle.rc_document_key,
		insurance: !!editingVehicle.insurance_document_key,
		// API fields
		vehicle_type_id: editingVehicle.vehicle_type_id, // Store ID, not label
		vehicle_type_name: vehicleTypeName, // Store name for display
		vehicle_number: editingVehicle.vehicle_number,
		rc_document_key: editingVehicle.rc_document_key,
		rc_valid_till: editingVehicle.rc_valid_till
			? momentInstance(editingVehicle.rc_valid_till).format(dateFormat)
			: null,
		insurance_document_key: editingVehicle.insurance_document_key,
		insurance_valid_till: editingVehicle.insurance_valid_till
			? momentInstance(editingVehicle.insurance_valid_till).format(dateFormat)
			: null,
		puc_document_key: editingVehicle.puc_document_key,
		puc_valid_till: editingVehicle.puc_valid_till
			? momentInstance(editingVehicle.puc_valid_till).format(dateFormat)
			: null,
		ownership_type: editingVehicle.ownership_type,
		consent_letter_key: editingVehicle.consent_letter_key,
		gps_installed: editingVehicle.gps_installed,
		gps_provider_name:
			editingVehicle.gps_installed === true ? editingVehicle.gps_provider_name : null,
		skip_document_upload: isPersistedVehicle(selectedVehicle)
			? false
			: !!editingVehicle.skip_document_upload,
	};

	const updatedVehicles = vehicles.map((vehicle) =>
		vehicle.key === selectedVehicle.key ? updatedVehicle : vehicle
	);

	setFieldValue('vehicles', updatedVehicles);
	messageInstance.success('Vehicle updated successfully');
	handleModalClose();
};
